import { useState } from "react";
import { CalendarIcon } from "lucide-react";
import { format, addHours } from "date-fns";
import { sv } from "date-fns/locale";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const TIME_SLOTS = [
  "09:00",
  "10:00",
  "11:00",
  "13:00",
  "14:00",
  "15:00",
  "16:00",
];

interface Props {
  memberName?: string;
  onSelect: (text: string) => void;
}

export function MeetingTimePicker({ memberName, onSelect }: Props) {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState<Date | undefined>(undefined);
  const [time, setTime] = useState<string | null>(null);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const confirm = () => {
    if (!date || !time) return;
    const [h, m] = time.split(":").map(Number);
    const start = new Date(date);
    start.setHours(h!, m!, 0, 0);
    const end = addHours(start, 1);

    const dayStr = format(date, "EEEE d MMMM", { locale: sv });
    const endTime = format(end, "HH:mm");
    const withMember = memberName ? ` med ${memberName}` : "";
    const text = `Boka möte${withMember} ${dayStr} kl ${time}–${endTime}`;

    onSelect(text);
    setOpen(false);
    setDate(undefined);
    setTime(null);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <CalendarIcon className="h-3 w-3" />
          Välj tid
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="p-3 pb-0">
          <p className="text-xs font-medium text-foreground">
            {memberName ? `Boka möte med ${memberName}` : "Välj datum och tid"}
          </p>
        </div>
        <Calendar
          mode="single"
          selected={date}
          onSelect={setDate}
          disabled={{ before: today }}
          locale={sv}
          weekStartsOn={1}
        />
        <div className="border-t px-3 pb-3 pt-2">
          <p className="mb-1.5 text-[10px] uppercase tracking-wide text-muted-foreground">
            Tid
          </p>
          <div className="grid grid-cols-4 gap-1">
            {TIME_SLOTS.map((slot) => (
              <button
                key={slot}
                type="button"
                onClick={() => setTime(slot)}
                className={cn(
                  "rounded-md border px-1.5 py-1 text-xs transition-colors",
                  time === slot
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border bg-background hover:bg-muted",
                )}
              >
                {slot}
              </button>
            ))}
          </div>
          <Button
            size="sm"
            className="mt-3 w-full"
            disabled={!date || !time}
            onClick={confirm}
          >
            {date && time
              ? `Välj ${format(date, "d MMM", { locale: sv })} kl ${time}`
              : "Välj datum och tid"}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
